/** Points-to-grid fragment shader. */

/** @todo Use a better setup than this, for tidier ongoing mapping tests. */
#define test_off 0
#define test_p2g 1
#define test_draw 2
#ifndef test
  #define test test_off
#endif

#ifdef GL_EXT_draw_buffers
  #extension GL_EXT_draw_buffers : require
#endif

precision highp float;

/** @todo Draw to all the needed grid passes. */

#if defined(grid_output_motionFull) || defined(grid_output_motionBase)
  uniform vec4 grid_stateShape;
#endif

#ifdef rescale_motion
  uniform vec2 rescaleMotion;
#else
  const float rescaledMotion = 1.0;
#endif
#ifdef rescale_mass
  uniform vec2 rescaleMass;
#else
  const float rescaledMass = 1.0;
#endif
#ifdef rescale_affine
  uniform vec2 rescaleAffine;
#else
  const float rescaledAffine = 1.0;
#endif
#ifdef rescale_stress
  uniform vec2 rescaleStress;
#else
  const float rescaledStress = 1.0;
#endif

#if test == test_p2g
  varying vec4 color;
#endif

varying vec2 cellAt;
varying vec2 cellBase;
varying mat3 cellWeights;

#ifdef points_grid_quads
  varying vec2 cellIndex;
#else
  varying vec3 point;
#endif

#if defined(grid_output_mass) || defined(grid_output_motionFull) || \
    defined(grid_output_motionBase)
  varying float mass;
#endif
#if defined(grid_output_motionFull) || defined(grid_output_motionBase)
  varying mat2 affine;
  varying vec2 motion;
#endif
#if defined(grid_output_motionFull)
  varying mat2 stress;
#endif

const mat3 mask = mat3(vec3(0), vec3(1), vec3(2));

const vec2 v1 = vec2(1);
const vec2 v0 = v1*0.0;
const vec2 cellNear = v1*0.5;

#ifndef points_grid_quads
  #pragma glslify: spreadUV = require(@epok.tech/gl-gpgpu/src/lookup/spread-uv);
#endif

#if defined(grid_output_motionFull) || defined(grid_output_motionBase)
  #pragma glslify: toKernelOffset = require(../kernel/to-offset);
  #pragma glslify: toKernelDistance = require(../kernel/to-distance);
#endif

#pragma glslify: lt = require(glsl-conditionals/when_lt);
#pragma glslify: map = require(glsl-map);

#if defined(rescale_motion) || defined(rescale_mass) || \
  defined(rescale_affine) || defined(rescale_stress)
  #pragma glslify: exponent = require(../vector/exponent);
#endif

void main() {
  #if test == test_p2g
    // for(int t = 0; t < grid_textures; ++t) { gl_FragData[t] = color; }
    gl_FragData[0] = color;
    return;
  #endif

  #ifndef points_grid_quads
    /**
     * Convert vertex position to `gl_FragCoord` window-space.
     * Mask the weights by the distance from these indexes per-fragment.
     * Account for the half-pixel offset at the fragment centre.
     * @see [Pixel coordinates on WebGL Fundamentals](https://webglfundamentals.org/webgl/lessons/webgl-shadertoy.html#pixel-coords)
     * @see [SO](https://stackoverflow.com/a/7158573)
     * @see [SO](https://stackoverflow.com/a/54237532/716898)
     */
    vec2 cellIndex = map(gl_FragCoord.xy-point.xy, -point.bb, point.bb, v0, v1);

    cellIndex = spreadUV(cellIndex, kernelSize);
  #endif

  /** Work around the need for constant index by looping and masking. */
  // float weight = cellWeights[int(cellIndex.x)].x*cellWeights[int(cellIndex.y)].y;
  vec2 maskWeight = v0;

  for(int i = 0; i < kernelSize; ++i) {
    maskWeight += cellWeights[i].xy*lt(abs((cellIndex)-mask[i].xy), cellNear);
  }

  float weight = maskWeight.x*maskWeight.y;

  /** @todo Handle multiple passes properly. */
  #if defined(grid_output_mass) || defined(grid_output_motionFull) || \
      defined(grid_output_motionBase)
    #ifdef rescale_mass
      float rescaledMass = exponent(rescaleMass);
    #endif

    float massTo = rescaledMass*mass;
  #endif
  #if defined(grid_output_motionFull) || defined(grid_output_motionBase)
    #ifdef rescale_motion
      float rescaledMotion = exponent(rescaleMotion);
    #endif
    #ifdef rescale_affine
      float rescaledAffine = exponent(rescaleAffine);
    #endif

    vec2 motionTo = rescaledMotion*motion;
    mat2 affineTo = rescaledAffine*affine;
    vec2 massMotion = massTo*motionTo;
    vec2 offset = toKernelOffset(cellIndex);

    vec2 cellDist =
      toKernelDistance(offset, cellAt, cellBase)/grid_stateShape.xy;
  #endif
  #if defined(grid_output_motionFull)
    #ifdef rescale_stress
      float rescaledStress = exponent(rescaleStress);
    #endif

    mat2 stressTo = rescaledStress*stress;
  #endif

  #ifdef grid_output_mass
    grid_output_mass = (weight*massTo)/rescaledMass;
  #endif
  #ifdef grid_output_motionFull
    grid_output_motionFull =
      (weight*(massMotion+((stressTo+affineTo)*cellDist)))/rescaledMotion;
  #endif
  #ifdef grid_output_motionBase
    grid_output_motionBase =
      (weight*(massMotion+(affineTo*cellDist)))/rescaledMotion;
  #endif
}
